import StructuredData from '@components/app/StructuredData'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faList } from '@fortawesome/pro-regular-svg-icons/faList'
import { faThumbsUp } from '@fortawesome/pro-regular-svg-icons/faThumbsUp'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck'
import { faRobot } from '@fortawesome/pro-solid-svg-icons/faRobot'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import getEnrichedTextInMarkdown from '@lib/utilities/enrichTextInMarkdown/getEnrichedTextInMarkdown'
import { useCallback, useState } from 'react'
import { InView } from 'react-intersection-observer'
import ReactMarkdown from 'react-markdown'
import { jsonLdScriptProps } from 'react-schemaorg'
import remarkGfm from 'remark-gfm'
import type { FAQPage } from 'schema-dts'
import {
  type FaqEntity,
  type FaqToRender,
  buildFaqEntity,
} from './buildFaqEntity'

/**
 * Render an FAQ section, includes LD+JSON support.
 *
 * @param faqs An array of FAQ objects
 * @param faqName A string representing the name for Google
 */
export default function Faqs({
  faqName,
  faqs,
  questionElement = 'h4',
}: {
  faqName: string
  faqs: FaqToRender[]
  questionElement?: 'h3' | 'h4'
}) {
  const [activeUpvotePills, setActiveUpvotePills] = useState<string[]>([])

  const [expanded, setExpanded] = useState<boolean>(true)

  const faqEntities = faqs.reduce((faqEntities: FaqEntity[], faq) => {
    faqEntities.push(buildFaqEntity(faq))
    return faqEntities
  }, [])

  // `faqEntities` contains escaped HTML entities, as per
  // https://www.w3.org/TR/json-ld11/#restrictions-for-contents-of-json-ld-script-elements
  const scriptTagPropsForJsonLd = jsonLdScriptProps<FAQPage>({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqEntities,
    'name': faqName,
  })

  const isActiveUpvote = useCallback(
    (faqId: string) => activeUpvotePills.includes(faqId),
    [activeUpvotePills],
  )

  const trackFaqUpvoteClick = useCallback(
    (faqId: string) => {
      if (isActiveUpvote(faqId)) {
        setActiveUpvotePills(
          activeUpvotePills.filter((activeFaqId) => activeFaqId !== faqId),
        )
      } else {
        setActiveUpvotePills(activeUpvotePills.concat(faqId))
      }
      trackEvent(TrackingEvents.FAQ_UPVOTE_CLICKED, {
        source: TrackingSources.FAQ,
        type: 'ClinicalTrialFaqId',
        val: faqId,
      })
    },
    [activeUpvotePills, isActiveUpvote, setActiveUpvotePills],
  )

  return (
    <>
      <StructuredData
        id={`${faqName.toLowerCase().replace(/\s+/gs, '-')}-faqs`}
        scriptTagProps={scriptTagPropsForJsonLd}
      />
      <Text
        className={'mb-2 text-neutral600'}
        styleName='p-small'
        value={
          'These questions and answers are submitted by anonymous patients, and have not been verified by our internal team.'
        }
      />
      <div className='grid w-full grid-cols-1 gap-4'>
        <InView
          onChange={(inView) => {
            if (inView) setExpanded(false)
          }}
          triggerOnce
        >
          <div className='flex flex-col space-y-2'>
            {faqs.map((faq, key) => (
              <FAQBlock
                expanded={expanded}
                faq={faq}
                index={key}
                isActiveUpvote={isActiveUpvote}
                key={key}
                questionElement={questionElement}
                trackFaqUpvoteClick={trackFaqUpvoteClick}
              />
            ))}
          </div>
        </InView>
        <div className='flex flex-col-reverse md:flex-row'>
          <Button
            className={classMerge(
              buttonClassNames.legacy.accent.neutral,
              'ml-auto rounded-full',
            )}
            onClick={() => {
              setExpanded(!expanded)
            }}
            value={
              <Text
                value={
                  <>
                    <Icon className='mr-2' icon={faList} />
                    {!expanded ? 'View All' : 'Hide'}
                  </>
                }
              />
            }
          />
        </div>
      </div>
    </>
  )
}

const FAQBlock = ({
  expanded,
  faq,
  index,
  isActiveUpvote,
  questionElement,
  trackFaqUpvoteClick,
}: {
  expanded: boolean
  faq: FaqToRender
  index: number
  isActiveUpvote: (faqId: string) => boolean
  questionElement?: 'h3' | 'h4'
  trackFaqUpvoteClick: (faqId: string) => void
}) => {
  return (
    <div
      className={classMerge(
        'w-full rounded-2xl border border-neutral200 bg-neutral50 p-4 sm:p-6',
        !expanded && index > 0 && 'hidden',
      )}
    >
      <div className='flex flex-col justify-between sm:flex-row'>
        <div>
          <Text
            element={questionElement}
            styleName='p-strong'
            value={faq.question}
          />
          <Text
            className='text-neutral600'
            styleName='p'
            value={
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {`"${getEnrichedTextInMarkdown(faq.answer)}"`}
              </ReactMarkdown>
            }
          />
          <FaqAuthor source={faq.source} />
        </div>
        <div className='flex items-start justify-end'>
          <span
            className={classMerge(
              'mx-2 cursor-pointer rounded-2xl border px-6 py-2 ',
              isActiveUpvote(faq.id.toString())
                ? 'border-primary600 bg-primary50 text-primary600'
                : 'border-neutral400 bg-neutral50 text-neutral600',
            )}
            onClick={() => trackFaqUpvoteClick(faq.id.toString())}
          >
            <Icon icon={faThumbsUp} />
          </span>
        </div>
      </div>
    </div>
  )
}

const FaqAuthor = ({ source }: { source?: string }) => {
  if (source === 'imported') return null

  if (source === 'verified') {
    return (
      <div className='mt-1 flex'>
        <Icon
          className='mr-2 rounded-full text-success500'
          icon={faBadgeCheck}
          size='2x'
        />
        <Text styleName='p-small-strong' value='Verified Answer' />
      </div>
    )
  }

  return (
    <div className='mt-1 flex'>
      <Icon
        className='mr-2 rounded-full bg-blue500 px-[6px] py-2 text-white'
        icon={faRobot}
      />
      <Text styleName='p-small-strong' value='Answered by AI' />
    </div>
  )
}
